import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  CreateDatasetRequest,
  DatasetDetail,
  DatasetSummary,
  DeleteDatasetResponse,
  GetDatasetsRequest,
  GetDatasetsResponse,
  UpdateDatasetRequest,
} from '@mai/platform-api/src/routes/datasets.schema';

import { type QueryOptions, apiClient } from './axios';

// Type definitions from platform API schemas
export type GetDatasetsRequestType = GetDatasetsRequest;
export type { CreateDatasetRequest, DatasetDetail, DatasetSummary };

// API functions
export const fetchDatasets = async (
  params?: GetDatasetsRequestType,
): Promise<GetDatasetsResponse> => {
  const response = await apiClient.get('/datasets', { params });
  return response.data;
};

export const fetchDatasetsByTeamId = async (
  teamId: string,
): Promise<GetDatasetsResponse> => {
  return fetchDatasets({ teamIds: [teamId] });
};

export const fetchDatasetById = async (
  datasetId: string,
): Promise<DatasetDetail> => {
  const response = await apiClient.get(`/datasets/${datasetId}`);
  return response.data;
};

export const createDataset = async (
  data: CreateDatasetRequest,
): Promise<DatasetDetail> => {
  const response = await apiClient.post('/datasets', data);
  return response.data;
};

export const updateDataset = async (
  datasetId: string,
  data: UpdateDatasetRequest,
): Promise<DatasetDetail> => {
  const response = await apiClient.patch(`/datasets/${datasetId}`, data);
  return response.data;
};

export const deleteDataset = async (
  datasetId: string,
): Promise<DeleteDatasetResponse> => {
  const response = await apiClient.delete(`/datasets/${datasetId}`);
  return response.data;
};

// React Query hooks
export const useDatasets = (
  params?: GetDatasetsRequestType,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['datasets', params],
    queryFn: () => fetchDatasets(params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    retry: options?.retry,
  });
};

export const useDatasetsByTeamId = (
  teamId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['datasets', 'by-team', teamId],
    queryFn: () => fetchDatasetsByTeamId(teamId!),
    enabled: !!teamId && options?.enabled !== false,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const useDatasetById = (
  datasetId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['datasets', 'by-id', datasetId],
    queryFn: () => fetchDatasetById(datasetId!),
    enabled: !!datasetId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useCreateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDataset,
    onSuccess: () => {
      // Invalidate all datasets queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};

export const useUpdateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      datasetId,
      data,
    }: {
      datasetId: string;
      data: UpdateDatasetRequest;
    }) => updateDataset(datasetId, data),
    onSuccess: () => {
      // Invalidate all datasets queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};

export const useDeleteDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDataset,
    onSuccess: () => {
      // Invalidate all datasets queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
};
